import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { initLoginPost, loginPost, logoutPost } from "../api/auth/authApi"; // logoutPost 추가
import { setAccessToken } from "../util/jwtUtil";
import { setPublicKey } from "../util/publicKey";

const parseJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Invalid Token", error);
    return null;
  }
};

const getLoginInfoFromToken = (token) => {
  return parseJWT(token);
};

// JWT에서 테넌트 정보 추출
const extractTenantInfo = (loginInfo) => {
  if (!loginInfo?.tenantId) return null;
  return {
    tenantId: loginInfo.tenantId,
    groupAccess: loginInfo.groupAccess || false,
    homeTenantId: loginInfo.homeTenantId || null,
  };
};

// Initial state
const initState = {
  accessToken: null,
  loginInfo: null, // Store the whole loginInfo object
  // 계열사 그룹 멀티테넌시 정보
  tenantInfo: null, // { tenantId, groupAccess, homeTenantId }
  // 2FA 관련 상태
  twoFactorRequired: false,
  twoFactorTempToken: null,
  twoFactorInfo: null, // { method, setupCompleted, email, memberName, isAdminOrSuper, isSuperAccount }
  // SUPER 계정 담당자 선택 상태
  selectedDelegate: null, // { delegateSeq, name, email, method }
};

// 로그인페이지 초기화 비동기 호출
export const initLoginPostAsync = createAsyncThunk(
  "initLoginPostAsync",
  async (_, thunkAPI) => {
    const response = await initLoginPost();
    if (response.publicKey) {
      setPublicKey(response.publicKey);
      thunkAPI.dispatch(initLogin());
    }
  }
);

// 로그인 비동기 호출
export const loginPostAsync = createAsyncThunk(
  "loginPostAsync",
  async (param, thunkAPI) => {
    const response = await loginPost(param);

    // 2FA가 필요한 경우
    if (response.twoFactorRequired) {
      // 2FA 응답에 포함된 RSA 공개키 저장 (코드 암호화용)
      if (response.publicKey) {
        setPublicKey(response.publicKey);
      }
      thunkAPI.dispatch(setTwoFactorRequired({
        tempToken: response.tempToken,
        twoFactorInfo: {
          method: response.method,
          setupCompleted: response.setupCompleted,
          needsSetup: response.needsSetup,
          email: response.email,
          memberName: response.memberName,
          isAdminOrSuper: response.isAdminOrSuper,  // 관리자 여부 (ADMIN은 모든 방식 허용으로 변경)
          isSuperAccount: response.isSuperAccount || false,  // SUPER 계정 여부 (담당자 선택 필요)
        }
      }));
      return response;
    }

    // 2FA 불필요 - 바로 JWT 발급
    if (response.accessToken) {
      const loginInfo = getLoginInfoFromToken(response.accessToken);
      thunkAPI.dispatch(setLoginInfo(loginInfo)); // Dispatch action to set loginInfo
      setAccessToken(response.accessToken); // 메모리에 accessToken 설정
    }
    return response;
  }
);

// 로그아웃 비동기 호출
export const logoutPostAsync = createAsyncThunk(
  "logoutPostAsync",
  async (_, thunkAPI) => {
    await logoutPost(); // API를 통해 로그아웃 처리
    thunkAPI.dispatch(logout()); // 상태를 초기화
  }
);

const loginSlice = createSlice({
  name: "LoginSlice",
  initialState: initState,
  reducers: {
    initLogin: (state) => {
      setAccessToken(null);
      return { ...initState };
    },
    login: (state, action) => {
      const loginInfo = getLoginInfoFromToken(action.payload.accessToken);
      setAccessToken(action.payload.accessToken);
      return {
        ...initState,
        accessToken: action.payload.accessToken,
        loginInfo,
        tenantInfo: extractTenantInfo(loginInfo),
      };
    },
    logout: (state) => {
      setAccessToken(null); // 메모리에서 accessToken 삭제
      return { ...initState }; // 상태 초기화
    },
    setLoginInfo: (state, action) => {
      state.loginInfo = action.payload;
      // 초기 로그인 시에도 tenantInfo 설정 (계열사 전환 드롭다운에서 사용)
      state.tenantInfo = extractTenantInfo(action.payload);
    },
    // 2FA 관련 리듀서
    setTwoFactorRequired: (state, action) => {
      state.twoFactorRequired = true;
      state.twoFactorTempToken = action.payload.tempToken;
      state.twoFactorInfo = action.payload.twoFactorInfo;
    },
    clearTwoFactor: (state) => {
      state.twoFactorRequired = false;
      state.twoFactorTempToken = null;
      state.twoFactorInfo = null;
      state.selectedDelegate = null;
    },
    // SUPER 계정 담당자 선택 관련 리듀서
    setSelectedDelegate: (state, action) => {
      state.selectedDelegate = action.payload; // { delegateSeq, name, email, method }
    },
    clearSelectedDelegate: (state) => {
      state.selectedDelegate = null;
    },
    // 2FA 초기화 후 재설정 필요 상태로 변경
    resetTwoFactorSetup: (state) => {
      if (state.twoFactorInfo) {
        state.twoFactorInfo = {
          ...state.twoFactorInfo,
          setupCompleted: false,
          needsSetup: true,
          method: null,
        };
      }
    },
    completeTwoFactorLogin: (state, action) => {
      const loginInfo = getLoginInfoFromToken(action.payload.accessToken);
      setAccessToken(action.payload.accessToken);
      return {
        ...initState,
        accessToken: action.payload.accessToken,
        loginInfo,
        tenantInfo: extractTenantInfo(loginInfo),
      };
    },
    // 계열사 전환 시 테넌트 정보 수동 설정
    setTenantInfo: (state, action) => {
      state.tenantInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initLoginPostAsync.fulfilled, (state) => {})
      .addCase(initLoginPostAsync.rejected, (state) => {
        console.error("InitLogin failed");
      })
      .addCase(loginPostAsync.fulfilled, (state, action) => {
        const accessToken = action.payload.accessToken;
        if (accessToken) {
          setAccessToken(accessToken);
          state.accessToken = accessToken;
        }
      })
      .addCase(loginPostAsync.pending, (state) => {})
      .addCase(loginPostAsync.rejected, (state) => {})
      .addCase(logoutPostAsync.fulfilled, (state) => {})
      .addCase(logoutPostAsync.rejected, (state) => {});
  },
});

export const {
  initLogin,
  login,
  logout,
  setLoginInfo,
  setTenantInfo,
  setTwoFactorRequired,
  clearTwoFactor,
  resetTwoFactorSetup,
  completeTwoFactorLogin,
  setSelectedDelegate,
  clearSelectedDelegate,
} = loginSlice.actions;
export default loginSlice.reducer;
